import { useState, useEffect } from "react";
import { useTranslation } from "../contexts/TranslationContext";
import { fetchAPI } from "../APIClient/modules/baseApi";

export function useContactContent() {
  const { currentLanguage } = useTranslation();
  const [content, setContent] = useState({
    title: "",
    description: "",
    cardsHtml: "",
    metaTitle: "",
  });

  useEffect(() => {
    generateContactContent();
  }, [currentLanguage]);

  const generateContactContent = async () => {
    try {
      const response = await fetchAPI("/contact/page", { method: "GET" });
      const pageData = Array.isArray(response) ? response : [];

      const enData = pageData.find((row) => row.language === "en") || {};
      const viData = pageData.find((row) => row.language === "vi") || {};

      const currentData = currentLanguage === "en" ? enData : viData;
      const staticCards = getContactCardsHtml(currentLanguage);

      setContent({
        metaTitle: currentData.meta_title || "",
        title: currentData.title || "",
        description: currentData.description || "",
        cardsHtml: staticCards,
      });
    } catch (error) {
      console.error("Failed to fetch contact page content:", error);
      // Fallback to static content
      const contactContent = getContactContent(currentLanguage);
      setContent(contactContent);
    }
  };

  const getContactCardsHtml = (language) => {
    const socialLabel = language === "en" ? "Media social:" : "Mạng xã hội:";
    const socialRowHtml = `
                <div class="contact-row social-row">
                    <span class="contact-icon">🌐</span>
                    <div class="contact-text social-media-wrap">
                        <span class="social-label">${socialLabel}</span>
                        <div class="social-links">
                            <a href="https://www.facebook.com/profile.php?id=61585520233908" target="_blank" rel="noopener noreferrer" class="social-link social-facebook" aria-label="Facebook">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.03H7.9v-2.9h2.54V9.84c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.23.2 2.23.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.9h2.77l-.44 2.9h-2.33V22c4.78-.75 8.44-4.91 8.44-9.93z"/></svg>
                            </a>
                            <a href="https://www.linkedin.com/company/vietqubit" target="_blank" rel="noopener noreferrer" class="social-link social-linkedin" aria-label="LinkedIn">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M6.94 8.5a1.72 1.72 0 1 1 0-3.44 1.72 1.72 0 0 1 0 3.44zM8.42 19H5.45V9.75h2.97V19zm10.13 0h-2.96v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39V19H9.4V9.75h2.85v1.26h.04c.4-.75 1.37-1.54 2.82-1.54 3.02 0 3.58 1.99 3.58 4.57V19z"/></svg>
                            </a>
                            <a href="https://www.youtube.com/@vietqubit" target="_blank" rel="noopener noreferrer" class="social-link social-youtube" aria-label="YouTube">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.56A3.02 3.02 0 0 0 .5 6.2 31.7 31.7 0 0 0 0 12a31.7 31.7 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.56 9.38.56 9.38.56s7.5 0 9.38-.56a3.02 3.02 0 0 0 2.12-2.14A31.7 31.7 0 0 0 24 12a31.7 31.7 0 0 0-.5-5.8zM9.6 15.68V8.32L15.85 12 9.6 15.68z"/></svg>
                            </a>
                        </div>
                    </div>
                </div>
    `;

    if (language === "en") {
      return `
            <!-- Card 1 -->
            <div class="contact-card">
                <h2 class="contact-title">Customer service</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Support for topics related to research, collaboration, and funding.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Business hours:<br>
                        • Monday – Friday<br>
                        • 9:00 AM – 4:00 PM (Atlantic Time, New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Email:
                        <a href="mailto:info@vietqubit.com" class="contact-link">info@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>

            <!-- Card 2 -->
            <div class="contact-card">
                <h2 class="contact-title">Media services</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Support for media relations and events.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Business hours:<br>
                        • Monday – Friday<br>
                        • 9:00 AM – 4:00 PM (Atlantic Time, New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Email:
                        <a href="mailto:events@vietqubit.com" class="contact-link">events@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>
        `;
    }

    return `
            <!-- Card 1 -->
            <div class="contact-card">
                <h2 class="contact-title">Dịch vụ khách hàng</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Hỗ trợ các vấn đề liên quan đến nghiên cứu, hợp tác, và tài trợ.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Giờ làm việc:<br>
                        • Thứ 2 - Thứ 6<br>
                        • 9:00 AM – 4:00 PM (giờ Atlantic, tỉnh New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Thư điện tử:
                        <a href="mailto:info@vietqubit.com" class="contact-link">info@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>

            <!-- Card 2 -->
            <div class="contact-card">
                <h2 class="contact-title">Dịch vụ truyền thông</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Hỗ trợ các vấn đề liên quan đến truyền thông và sự kiện.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Giờ làm việc:<br>
                        • Thứ 2 - Thứ 6<br>
                        • 9:00 AM – 4:00 PM (giờ Atlantic, tỉnh New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Thư điện tử:
                        <a href="mailto:events@vietqubit.com" class="contact-link">events@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>
        `;
  };

  const getContactContent = (language) => {
    const contents = {
      vi: getVietnameseContact(),
      en: getEnglishContact(),
    };
    return contents[language] || contents.vi;
  };

  const getVietnameseContact = () => {
    const socialRowHtml = `
                <div class="contact-row social-row">
                    <span class="contact-icon">🌐</span>
                    <div class="contact-text social-media-wrap">
                        <span class="social-label">Mạng xã hội:</span>
                        <div class="social-links">
                            <a href="https://www.facebook.com/profile.php?id=61585520233908" target="_blank" rel="noopener noreferrer" class="social-link social-facebook" aria-label="Facebook">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.03H7.9v-2.9h2.54V9.84c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.23.2 2.23.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.9h2.77l-.44 2.9h-2.33V22c4.78-.75 8.44-4.91 8.44-9.93z"/></svg>
                            </a>
                            <a href="https://www.linkedin.com/company/vietqubit" target="_blank" rel="noopener noreferrer" class="social-link social-linkedin" aria-label="LinkedIn">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M6.94 8.5a1.72 1.72 0 1 1 0-3.44 1.72 1.72 0 0 1 0 3.44zM8.42 19H5.45V9.75h2.97V19zm10.13 0h-2.96v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39V19H9.4V9.75h2.85v1.26h.04c.4-.75 1.37-1.54 2.82-1.54 3.02 0 3.58 1.99 3.58 4.57V19z"/></svg>
                            </a>
                            <a href="https://www.youtube.com/@vietqubit" target="_blank" rel="noopener noreferrer" class="social-link social-youtube" aria-label="YouTube">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.56A3.02 3.02 0 0 0 .5 6.2 31.7 31.7 0 0 0 0 12a31.7 31.7 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.56 9.38.56 9.38.56s7.5 0 9.38-.56a3.02 3.02 0 0 0 2.12-2.14A31.7 31.7 0 0 0 24 12a31.7 31.7 0 0 0-.5-5.8zM9.6 15.68V8.32L15.85 12 9.6 15.68z"/></svg>
                            </a>
                        </div>
                    </div>
                </div>
    `;

    return {
      metaTitle: "Liên hệ | VietQubit",
      title: "Thông tin liên hệ",
      description:
        "VietQubit luôn luôn lắng nghe mọi ý kiến, câu hỏi, và cơ hội hợp tác từ bạn. Đội ngũ nghiên cứu luôn sẵn sàng trao đổi về các dự án AI, nhu cầu tư vấn giải pháp, cũng như những ý tưởng tiềm năng trong giáo dục, y tế, nông nghiệp, khí hậu và robot. Bạn có thể gửi thông tin qua email hoặc các kênh mạng xã hội được liệt kê trên trang. Mọi thông tin liên hệ sẽ được tiếp nhận và phản hồi trong thời gian sớm nhất, với tinh thần tôn trọng, minh bạch và hợp tác lâu dài.",
      cardsHtml: `
            <!-- Card 1 -->
            <div class="contact-card">
                <h2 class="contact-title">Dịch vụ khách hàng</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Hỗ trợ các vấn đề liên quan đến nghiên cứu, hợp tác, và tài trợ.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Giờ làm việc:<br>
                        • Thứ 2 - Thứ 6<br>
                        • 9:00 AM – 4:00 PM (giờ Atlantic, tỉnh New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Thư điện tử:
                        <a href="mailto:info@vietqubit.com" class="contact-link">info@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>

            <!-- Card 2 -->
            <div class="contact-card">
                <h2 class="contact-title">Dịch vụ truyền thông</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Hỗ trợ các vấn đề liên quan đến truyền thông và sự kiện.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Giờ làm việc:<br>
                        • Thứ 2 - Thứ 6<br>
                        • 9:00 AM – 4:00 PM (giờ Atlantic, tỉnh New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Thư điện tử:
                        <a href="mailto:events@vietqubit.com" class="contact-link">events@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>
        `,
    };
  };

  const getEnglishContact = () => {
    const socialRowHtml = `
                <div class="contact-row social-row">
                    <span class="contact-icon">🌐</span>
                    <div class="contact-text social-media-wrap">
                        <span class="social-label">Media social:</span>
                        <div class="social-links">
                            <a href="https://www.facebook.com/profile.php?id=61585520233908" target="_blank" rel="noopener noreferrer" class="social-link social-facebook" aria-label="Facebook">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.03H7.9v-2.9h2.54V9.84c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.23.2 2.23.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.9h2.77l-.44 2.9h-2.33V22c4.78-.75 8.44-4.91 8.44-9.93z"/></svg>
                            </a>
                            <a href="https://www.linkedin.com/company/vietqubit" target="_blank" rel="noopener noreferrer" class="social-link social-linkedin" aria-label="LinkedIn">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M6.94 8.5a1.72 1.72 0 1 1 0-3.44 1.72 1.72 0 0 1 0 3.44zM8.42 19H5.45V9.75h2.97V19zm10.13 0h-2.96v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39V19H9.4V9.75h2.85v1.26h.04c.4-.75 1.37-1.54 2.82-1.54 3.02 0 3.58 1.99 3.58 4.57V19z"/></svg>
                            </a>
                            <a href="https://www.youtube.com/@vietqubit" target="_blank" rel="noopener noreferrer" class="social-link social-youtube" aria-label="YouTube">
                                <svg viewBox="0 0 24 24" aria-hidden="true" class="social-icon-svg"><path d="M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.56A3.02 3.02 0 0 0 .5 6.2 31.7 31.7 0 0 0 0 12a31.7 31.7 0 0 0 .5 5.8 3.02 3.02 0 0 0 2.12 2.14c1.88.56 9.38.56 9.38.56s7.5 0 9.38-.56a3.02 3.02 0 0 0 2.12-2.14A31.7 31.7 0 0 0 24 12a31.7 31.7 0 0 0-.5-5.8zM9.6 15.68V8.32L15.85 12 9.6 15.68z"/></svg>
                            </a>
                        </div>
                    </div>
                </div>
    `;

    return {
      metaTitle: "Contact | VietQubit",
      title: "Contact information",
      description:
        "VietQubit always welcomes your feedback, questions, and collaboration opportunities. Our research team is ready to discuss AI projects, solution consulting needs, and promising ideas in education, healthcare, agriculture, climate, and robotics. You can reach us via email or the social channels listed on this page. All inquiries will be handled and answered as soon as possible, with respect, transparency, and a long‑term partnership mindset.",
      cardsHtml: `
            <!-- Card 1 -->
            <div class="contact-card">
                <h2 class="contact-title">Customer service</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Support for topics related to research, collaboration, and funding.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Business hours:<br>
                        • Monday – Friday<br>
                        • 9:00 AM – 4:00 PM (Atlantic Time, New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Email:
                        <a href="mailto:info@vietqubit.com" class="contact-link">info@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>

            <!-- Card 2 -->
            <div class="contact-card">
                <h2 class="contact-title">Media services</h2>

                <div class="contact-row">
                    <span class="contact-icon">🤝</span>
                    <p class="contact-text">
                        Support for media relations and events.
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">⏰</span>
                    <p class="contact-text">
                        Business hours:<br>
                        • Monday – Friday<br>
                        • 9:00 AM – 4:00 PM (Atlantic Time, New Brunswick, Canada)
                    </p>
                </div>

                <div class="contact-row">
                    <span class="contact-icon">✉️</span>
                    <p class="contact-text">
                        Email:
                        <a href="mailto:events@vietqubit.com" class="contact-link">events@vietqubit.com</a>
                    </p>
                </div>

                ${socialRowHtml}
            </div>
        `,
    };
  };

  return content;
}
